// eslint-disable-next-line no-restricted-imports
import { ThemeProvider as _CompatibilityEmotionThemeProvider } from "@emotion/react";
import type { MantineTheme, MantineThemeOverride } from "@mantine/core";
import { MantineProvider } from "@mantine/core";
import { merge } from "icepick";
// DX-Comment: Importing useEffect and useState to capture brand color dynamically
import { type ReactNode, useContext, useMemo, useEffect, useState } from "react";

import type { ColorName } from "metabase/ui/colors/types";
import type { ResolvedColorScheme } from "metabase/utils/color-scheme";
import { getCspNonce } from "metabase/utils/csp";
import type { ColorSettings } from "metabase-types/api";

import { getThemeOverrides } from "../../../theme";
import { useColorScheme } from "../ColorSchemeProvider";
import { DatesProvider } from "../DatesProvider";

import { ThemeProviderContext } from "./context";
// DX-Comment: Importing colors to set brand color dynamically
import { deriveFullMetabaseTheme, mutateColors } from "metabase/ui/colors";

export interface ThemeProviderProps {
  children: ReactNode;

  /**
   * Extend Metabase's theme overrides.
   * This is primarily used in the React embedding SDK
   * to allow SDK users to customize the theme.
   */
  theme?: MantineThemeOverride;

  /**
   * Resolved color scheme to use. If not provided, reads from ColorSchemeContext.
   */
  resolvedColorScheme?: ResolvedColorScheme;

  /**
   * Whitelabel color settings. If not provided, defaults to undefined (no whitelabel).
   */
  whitelabelColors?: ColorSettings | null;

  /**
   * Callback invoked when whitelabel colors should be updated.
   */
  onUpdateWhitelabelColors?: (colors: ColorSettings) => void;

  /**
   * CSS selector for Mantine CSS variables injection.
   */
  cssVariablesSelector?: string;
}

// DX-Comment: Declaring global variable to hold brand color from DX web app
declare global {
  interface Window {
    METABASE_BRAND_COLOR?: string;
  }
}

export const ThemeProvider = ({
  children,
  theme: themeOverride,
  resolvedColorScheme: resolvedColorSchemeProp,
  whitelabelColors,
  onUpdateWhitelabelColors,
  cssVariablesSelector,
}: ThemeProviderProps) => {
  // DX-Comment Get brand color from portal, whitelabel, or use default green.
  const [portalBrandColor, setPortalBrandColor] = useState<string | undefined>(window.METABASE_BRAND_COLOR);

  //DX-Comment Add listener for postMessage to update brand color dynamically
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.action === 'updateBrandColor' && event.data.brandColor) {
        setPortalBrandColor(event.data.brandColor);
        window.METABASE_BRAND_COLOR = event.data.brandColor;
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const whitelabelBrandColor = whitelabelColors?.brand;
  
  // Priority: portal > whitelabel > default
  const effectiveBrandColor = 
    portalBrandColor || whitelabelBrandColor || "#51B848";

  //DX-Comment: Create combined color settings with portal brand color for proper theme derivation
  const combinedWhitelabelColors: ColorSettings | null | undefined = useMemo(() => {
    if (!effectiveBrandColor) return whitelabelColors;
    return {
      ...whitelabelColors,
      brand: effectiveBrandColor,
      accent0: effectiveBrandColor,
      summarize: effectiveBrandColor,
    };
  }, [effectiveBrandColor, whitelabelColors]);

  const colorSchemeContext = useColorScheme();
  const resolvedColorScheme =
    resolvedColorSchemeProp ?? colorSchemeContext.resolvedColorScheme;

  // Merge default theme overrides with user-provided theme overrides
  const theme = useMemo(() => {
    // DX-Comment: Get base theme overrides with combined whitelabel colors including portal brand
    const baseThemeOverrides = getThemeOverrides(
      resolvedColorScheme,
      combinedWhitelabelColors,
    );

    // DX-Comment: Merging base theme overrides with any user-provided theme overrides.
    const mergedTheme = merge(baseThemeOverrides, themeOverride) as MantineTheme;

    // DX-Comment: Return merged theme with color update function
    return {
      ...mergedTheme,
      primaryColor: 'brand', // DX-Comment: Force primary color to target the brand color array
      other: {
        ...mergedTheme.other,
        ...(onUpdateWhitelabelColors && {
          updateColorSettings: onUpdateWhitelabelColors,
        }),
      },
      fn: {
        themeColor: (color: ColorName): string => {
          const { primaryShade, primaryColor } = mergedTheme;

          return color in mergedTheme.colors
            ? mergedTheme.colors[color][primaryShade as number]
            : mergedTheme.colors[primaryColor as ColorName][
                primaryShade as number
              ];
        },
      },
    } as MantineTheme;
  }, [
    themeOverride,
    resolvedColorScheme,
    combinedWhitelabelColors,
    effectiveBrandColor, // DX-Comment: Add effectiveBrandColor as dependency to update theme when portal brand color changes
    onUpdateWhitelabelColors,
  ]);

  //DX-Comment: Update the colors object and legacy CSS variables when brand color changes
  useEffect(() => {
    if (!effectiveBrandColor) {
      return;
    }

    mutateColors(combinedWhitelabelColors || {});

    const rootStyle = document.documentElement.style;
    const brandVars = {
      "--mb-color-brand": effectiveBrandColor,
      "--mb-color-text-brand": effectiveBrandColor,
      "--mb-color-background-brand": `color-mix(in srgb, ${effectiveBrandColor} 10%, transparent)`,
      "--mb-color-background-hover": `color-mix(in srgb, ${effectiveBrandColor} 20%, transparent)`,
      "--mb-color-focus": theme.fn.themeColor("focus"),
      "--mb-color-border-brand": effectiveBrandColor,
      "--mb-color-summarize": effectiveBrandColor,
      "--mb-color-summarize-8": theme.fn.themeColor("summarize"),
      "--color-brand": effectiveBrandColor,
      "--color-brand-hover": `color-mix(in srgb, ${effectiveBrandColor} 85%, black)`,
      "--color-text-brand": effectiveBrandColor,
      "--color-border-brand": effectiveBrandColor,
      "--color-bg-brand": `color-mix(in srgb, ${effectiveBrandColor} 10%, transparent)`,
      "--color-bg-brand-hover": `color-mix(in srgb, ${effectiveBrandColor} 20%, transparent)`,
      "--color-primary": effectiveBrandColor,
      "--color-primary-hover": `color-mix(in srgb, ${effectiveBrandColor} 85%, black)`,
      "--color-text-primary": theme.fn.themeColor("text-primary"),
      "--color-focus": theme.fn.themeColor("focus"),
      "--color-nav-active": `color-mix(in srgb, ${effectiveBrandColor} 15%, transparent)`,
    } as const;

    Object.entries(brandVars).forEach(([cssVar, value]) => {
      if (value) {
        rootStyle.setProperty(cssVar, value);
      }
    });
  }, [
    combinedWhitelabelColors,
    effectiveBrandColor,
    resolvedColorScheme,
    theme,
  ]);

  const { withCssVariables, withGlobalClasses } =
    useContext(ThemeProviderContext);

  return (
    <MantineProvider
      theme={theme}
      forceColorScheme={resolvedColorScheme}
      getStyleNonce={() => getCspNonce() ?? "metabase"}
      classNamesPrefix="mb-mantine"
      cssVariablesSelector={cssVariablesSelector}
      withCssVariables={withCssVariables}
      withGlobalClasses={withGlobalClasses}
    >
      <_CompatibilityEmotionThemeProvider theme={theme}>
        <DatesProvider>{children}</DatesProvider>
      </_CompatibilityEmotionThemeProvider>
    </MantineProvider>
  );
};
